import { useState, useRef, useLayoutEffect } from "react";
import classNames from "classnames";
import { Loader } from "@components/Loader";
import { Tooltip } from '@components/new/Tooltip';
import styles from "./TextInputWithSuggestion.module.scss";
import TextInput from "@components/TextInput/TextInput";
import DropdownSimple from "@components/DropdownSimple";

interface TextInputProps<T> {
  value?: string;
  onChange?: (value: T | string) => void;
  onSelect?: (value: T) => void;
  onInput?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  hasError?: boolean;
  errorMessage?: string;
  name?: string;
  // ref?: Ref<HTMLInputElement>;
  title: string;
  disabled?: boolean;
  maxLength?: number;
  useSuggestions: (search: string) => {
    data?: T[];
    isLoading: boolean;
    isFetching: boolean;
    isFetched: boolean;
    isError: boolean;
    refetch: () => void;
  };
  noSuggestionsMessage?: React.ReactNode;
  getName?: (v: T) => string;
  getSelectionValue?: (v: T) => string;
  disabledMessage?: string;
}

const TooltipContainer = ({ disabledMessage, children }: {
  disabledMessage?: string,
  children?: string | JSX.Element | JSX.Element[],
}) => {
  if (disabledMessage) {
    return (
      <Tooltip
        action={<>{children}</>}
        position="top"
        text={disabledMessage}
      />
    )
  }
  return (<>{children}</>)
}

const TextInputWithSuggestion = <T = string>(props: TextInputProps<T>) => {
  // const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownWidth, setDropdownWidth] = useState<number>(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // const isFilled = props.value && props.value.length > 0;
  const { data: suggestions, isFetching } = props.useSuggestions(props.value ?? "");

  const handleFocus = () => {
    // setIsFocused(true);
    setIsOpen(true);
    if (props.onFocus) {
      props.onFocus();
    }
  };
  const handleBlur = () => {
    // setIsFocused(false);
    if (props.onBlur) {
      props.onBlur();
    }
  };

  const handleSelect = (item: T) => {
    if (props.onSelect) {
      props.onSelect(item);
    }
    if (props.onChange) {
      props.onChange(props.getSelectionValue ? props.getSelectionValue(item) : item);
    }
    setIsOpen(false);
  };

  // todo вынести в DropdownSimple
  useLayoutEffect(() => {
    if (dropdownRef.current) {
      const { offsetWidth } = dropdownRef.current;
      setDropdownWidth(offsetWidth);
    }
  }, [dropdownRef.current]);

  return (
    <div className={styles.root}>
      <DropdownSimple
        triggerContent={(
          <div
            ref={dropdownRef}
            className={classNames(styles.inputContainer)}
          >
            <TooltipContainer disabledMessage={props.disabled && props.disabledMessage || ""} >
              <TextInput
                // ref={props.ref}
                value={props.value}
                type="text"
                title={props.title}
                onFocus={handleFocus}
                onClick={(e) => {
                  setIsOpen(true)
                  e.stopPropagation();
                }}
                onBlur={handleBlur}
                name={props.name}
                onChange={(v) => {
                  if (props.onChange) {
                    props.onChange(v);
                  }
                  if (props.onInput) {
                    props.onInput(v);
                  }
                }}
                disabled={props.disabled}
                maxLength={props.maxLength}
                errorMessage={props.errorMessage}
                hasError={props.hasError}
              />
            </TooltipContainer>
          </div>
        )}
        dropdownContent={(
          <div
            className={styles.menu}
            style={{ width: dropdownWidth }}
          >
            <div className={styles.menuContent}>
              {(suggestions || []).map((item, idx) => (
                <div
                  className={styles.item}
                  key={String(idx) + item}
                  onClick={() => handleSelect(item)}
                >
                  {props.getName ? props.getName(item) : String(item)}
                </div>
              ))}
              {(isFetching || suggestions?.length) ? null : (props.noSuggestionsMessage ?? null)}
            </div>
            {isFetching ? (
              <div className={styles.loader}>
                <Loader alignment="center" size="xs" />
              </div>
            ) : null}
          </div>
        )}
        menuAlign="left"
        menuPosition="bottom"
        disabled={props.disabled}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
    </div>
  );
};

export default TextInputWithSuggestion;
